import React from 'react';
import {
  AutocompleteHeader,
  AutocompleteHighlight,
  AutocompleteItem,
  AutocompleteProductItem,
  WhiteInstantSearchBuilder,
} from '@white-nl/instantsearch-components';
import { ImageSlash } from '../components/ui/icons/sharp/regular';

const searchBarSelector = '#white-instant-search-search-box-global';

function waitForSearchBar(callback: (searchBar: HTMLElement) => void) {
  // Prevent running on checkout pages
  if (document.body.classList.contains('checkout-pages')) {
    return;
  }
  const interval = 200;
  function check(tryCount = 0) {
    const searchBar = document.querySelector(searchBarSelector) as HTMLElement;
    if (searchBar) {
      callback(searchBar);
    } else if (tryCount < 300) { // try for 1 minute
      setTimeout(() => check(tryCount + 1), interval);
    }
  }
  check();
}

waitForSearchBar((searchBar) => {
  const apiUrl = Craft.getSiteUrl('api/react/search');
  fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then((res) => res.json())
    .then((data) => {
      const {
        translates,
      } = data;

      const { catalog } = translates;
      const whiteInstantSearchSearchBar = new WhiteInstantSearchBuilder();
      const settings = (searchBar as HTMLDivElement).dataset;

      whiteInstantSearchSearchBar
        .addAutocompleteSearchingComponent([
          {
            settings: {
              placeholder: settings.searchPlaceholder,
              useRecentSearches: true,
              sources: [
                {
                  indexName: settings.brandsIndexName,
                  getQueryParams: (query, defaultFilters?) => ({
                    query,
                    filters: defaultFilters ? `AND (${defaultFilters})` : '',
                    hitsPerPage: 1,
                  }),
                  transformResponse: ({ hits }: { hits: any[] }) => (
                    hits[0].map((hit: any) => ({
                      ...hit,
                      url: `/${hit.uri}/`,
                      breadcrumbs: hit.breadcrumbs ? `in ${hit.breadcrumbs}` : null,
                    }))
                  ),
                  templates: {
                    header: () => (
                      <AutocompleteHeader
                        title={Craft.t(catalog, 'Brands')}
                      />
                    ),
                    item: (
                      {
                        item,
                        source,
                        components,
                        state,
                      },
                    ) => (
                      <AutocompleteItem
                        hit={item}
                        url={source.getItemUrl!({ item, state })!}
                        components={{ ...components, Highlight: AutocompleteHighlight }}
                        titleAttribute="title"
                        descriptionAttribute="breadcrumbs"
                        onFillQuery={() => source.onFillQuery({ item, state })}
                      />
                    ),
                  },
                },
                {
                  indexName: settings.categoriesIndexName,
                  getQueryParams: (query, defaultFilters?) => ({
                    query,
                    filters: defaultFilters ? `AND (${defaultFilters})` : '',
                    hitsPerPage: 3,
                  }),
                  transformResponse: ({ hits }: { hits: any[] }) => (
                    hits[0].map((hit: any) => ({
                      ...hit,
                      url: `/${hit.uri}/`,
                      breadcrumbs: hit.breadcrumbs ? `in ${hit.breadcrumbs}` : null,
                    }))
                  ),
                  templates: {
                    header: () => (
                      <AutocompleteHeader
                        title={Craft.t(catalog, 'Categories')}
                      />
                    ),
                    item: (
                      {
                        item,
                        source,
                        components,
                        state,
                      },
                    ) => (
                      <AutocompleteItem
                        hit={item}
                        url={source.getItemUrl!({ item, state })!}
                        components={{ ...components, Highlight: AutocompleteHighlight }}
                        titleAttribute="title"
                        descriptionAttribute="breadcrumbs"
                        onFillQuery={() => source.onFillQuery({ item, state })}
                      />
                    ),
                  },
                },
                {
                  getQueryParams: (query, defaultFilters?) => ({
                    query,
                    filters: defaultFilters ? `AND (${defaultFilters})` : '',
                    hitsPerPage: 3,
                  }),
                  transformResponse: ({ hits }: { hits: any[] }) => (
                    hits[0].map((hit: any) => ({
                      ...hit,
                      url: `/${hit.uri}/`,
                    }))
                  ),
                  templates: {
                    header: () => (
                      <AutocompleteHeader
                        title={Craft.t(catalog, 'Products')}
                      />
                    ),
                    item: (
                      {
                        item,
                        source,
                        components,
                        state,
                      },
                    ) => (
                      <AutocompleteProductItem
                        hit={item}
                        url={source.getItemUrl!({ item, state })!}
                        components={{ ...components, Highlight: AutocompleteHighlight }}
                        titleAttribute="title"
                        descriptionAttribute={['productBrand', '0', 'title']}
                        imageAttribute={['image', 'transforms', 'all', 'url']}
                        imagePlaceholder={(
                          <div className="aa-ItemIcon aa-ItemIcon--picture aa-ItemIcon--alignTop">
                            <ImageSlash
                              width={30}
                              height={30}
                            />
                          </div>
                        )}
                        onFillQuery={() => source.onFillQuery({ item, state })}
                      />
                    ),
                    footer: (
                      {
                        items,
                        state,
                      },
                    ) => (
                      <div>
                        {items.length >= 3 && (
                          <div className="autocomplete-all-results-link">
                            <a href={`${settings.searchUrl}?query=${state.query}`}>
                              Bekijk alles
                            </a>
                          </div>
                        )}
                      </div>
                    ),
                  },
                },
              ],
              onSubmit: (query: string) => {
                window.location.href = `${settings.searchUrl}?query=${query}`;
              },
            },
          },
        ], searchBarSelector)
        .withIdentity('white-instant-search-search-bar-root')
        .withSearchkit(
          settings.catalogIndexName ?? '',
          settings.publicUrl ?? '',
          settings.jwt ?? '',
          settings.refreshUrl ?? '',
        )
        .withoutRouting()
        .build()
        .show();
    });
});
